import {
  Alert,
  App,
  AutoComplete,
  Card,
  Form,
  Modal,
  Select,
  Typography
} from "antd"
import React, { useEffect, useRef, useState } from "react"
import type { BlockerFunction } from "react-router-dom"

import { TextInput } from "../../../components/Input/TextInput.tsx"
import { TimezoneSelect } from "../../../components/Shared/TimezoneSelect.tsx"
import usePrompt from "../../../hooks/use-prompt.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import { getUsers } from "../../TeamManagementPage/api/services.ts"
import { extractErrorMessage } from "../../../utils/handlers/handle-error.tsx"
import { getLegalEntities, updateBranchConfiguration } from "../api/services.ts"
import {
  createBranchThunk,
  fetchBranchDetail,
  fetchBranches,
  updateBranchThunk
} from "../redux/branches.slice.ts"

const DISCARD_CONFIRM_TITLE = "Discard branch details?"
const DISCARD_CONFIRM_BODY =
  "You have unsaved changes. Leaving will discard them."

const { Text } = Typography

// Real, selectable starting-settings modes (BR 09) — never silently copies
// legal entity, tax IDs, bank/payment accounts, credentials or issued
// documents in any of them.
const START_HQ_DEFAULTS = "hq_defaults"
const START_SEPARATE = "separate"

interface AddBranchModalProps {
  open: boolean
  onClose: () => void
  // Edit mode: pre-fills from the given branch and submits via
  // `updateBranchThunk` (+ the branch's own configuration endpoint for time
  // zone, which `BranchUpdateSerializer` does not carry) instead of creating
  // a new one. City and "Starting settings" have no update path on the wire
  // today, so they're omitted in this mode rather than shown non-functional.
  branchId?: string
  // Called after a successful save, in addition to (edit mode) or instead
  // of (create mode, where it defaults to `onClose`) closing the modal —
  // lets a caller like the setup wizard react to the save without owning
  // its own copy of the submit logic.
  onSaved?: () => void
}

interface UserOption {
  id: string
  first_name: string
  last_name: string
}

const STARTING_SETTINGS_HELP: Record<string, string> = {
  [START_HQ_DEFAULTS]:
    "Start with the configurations, required information and templates defined in Company HQ. A later Company HQ change applies to this branch automatically, for as long as it stays uncustomized.",
  [START_SEPARATE]:
    "Start from safe system defaults instead of Company HQ's. This branch will not automatically pick up later Company HQ changes — configure it directly."
}

export const AddBranchModal = ({
  open,
  onClose,
  branchId,
  onSaved
}: AddBranchModalProps) => {
  const isEditMode = Boolean(branchId)
  const dispatch = useAppDispatch()
  const { modal } = App.useApp()
  const [form] = Form.useForm()
  const gcc = useAppSelector((state) => state.branches.gcc)
  const existingBranches = useAppSelector(
    (state) => state.branches.managedBranches
  )
  const detail = useAppSelector((state) => state.branches.detail)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [managers, setManagers] = useState<UserOption[]>([])
  const [legalEntityNames, setLegalEntityNames] = useState<string[]>([])
  // Tracks real user edits only — distinct from the programmatic prefills
  // below, which must never themselves count as "unsaved changes" (that
  // would prompt to discard a freshly-opened, untouched form).
  const [dirty, setDirty] = useState(false)
  const prefillingRef = useRef(false)

  // "Starting settings" is one field in the UI but carries two different
  // meanings on the wire: the two fixed modes need no id, a source branch
  // needs its id — kept apart so `copy_from_branch_id` is never sent
  // unless the caller actually chose "copy".
  const [startingSettings, setStartingSettings] =
    useState<string>(START_HQ_DEFAULTS)

  const handleStartingSettingsChange = (value: string) => {
    setStartingSettings(value)
    if (!prefillingRef.current) setDirty(true)
  }

  useEffect(() => {
    if (!open) return
    prefillingRef.current = true
    form.resetFields()
    setStartingSettings(START_HQ_DEFAULTS)
    setError(null)
    setDirty(false)
    if (!isEditMode) {
      form.setFieldsValue({
        timezone: gcc?.multi_branch_enabled ? undefined : "UTC"
      })
    }
    prefillingRef.current = false
    getUsers({ page_size: 200 }).then((response) => {
      if (response.ok) {
        setManagers(response.data?.results || [])
      }
    })
    getLegalEntities().then((response) => {
      if (response.ok) {
        setLegalEntityNames(response.data?.legal_entity_names || [])
      }
    })
    if (branchId) {
      dispatch(fetchBranchDetail(branchId))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the form is reset each time the modal opens or the branch changes; everything else is read as it is at that moment
  }, [open, branchId])

  // Pre-fills once the branch's own detail (incl. its effective time zone,
  // which only the configuration endpoint carries) has loaded — kept as its
  // own effect since `fetchBranchDetail` above resolves after the reset.
  useEffect(() => {
    if (!open || !branchId || !detail || detail.id !== branchId) return
    prefillingRef.current = true
    form.setFieldsValue({
      name: detail.name,
      manager_id: detail.manager?.id,
      vertical: detail.vertical || undefined,
      legal_entity_name: detail.legal_entity_name || undefined,
      timezone: detail.configuration.timezone.value
    })
    prefillingRef.current = false
  }, [open, branchId, detail, form])

  const requestClose = () => {
    // Dismissal (X, Cancel, Escape) is held off entirely while a save is
    // in flight — "discard" would be untrue mid-request, and the OK
    // button's own `confirmLoading` only guards against re-clicking OK.
    if (submitting) return
    if (!dirty) {
      onClose()
      return
    }
    modal.confirm({
      title: DISCARD_CONFIRM_TITLE,
      content: DISCARD_CONFIRM_BODY,
      okText: "Discard changes",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      // "Keep editing" is the safe default — focused so Enter/Escape on
      // this confirmation itself never discards by accident.
      autoFocusButton: "cancel",
      onOk: () => onClose()
    })
  }

  // The same question the modal's own Cancel/X/Escape would ask, triggered
  // instead by an in-app navigation (sidebar, a link, browser back) while
  // this form is open and dirty — the app's shared navigation-blocker
  // primitive; see use-prompt.ts. A workspace switch that would otherwise
  // unmount this page re-navigates through the exact same router path, so
  // it is covered by the same blocker rather than a second mechanism.
  // `useBeforeUnload` inside it also covers refresh/tab-close for free.
  const shouldBlockNavigation: BlockerFunction = () =>
    open && dirty && !submitting
  const blocker = usePrompt(shouldBlockNavigation)
  const blockerRef = useRef(blocker)
  blockerRef.current = blocker

  useEffect(() => {
    if (blocker.state !== "blocked") return
    modal.confirm({
      title: DISCARD_CONFIRM_TITLE,
      content: DISCARD_CONFIRM_BODY,
      okText: "Discard changes",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      autoFocusButton: "cancel",
      onOk: () => blockerRef.current.proceed?.(),
      onCancel: () => blockerRef.current.reset?.()
    })
  }, [blocker.state, modal])

  const isFirstAdditionalBranch = existingBranches.length === 1
  const isCopyMode = !Object.prototype.hasOwnProperty.call(
    STARTING_SETTINGS_HELP,
    startingSettings
  )
  const copySource = isCopyMode
    ? existingBranches.find((b) => b.id === startingSettings)
    : undefined

  const handleSubmit = async () => {
    setError(null)
    try {
      const values = await form.validateFields()
      setSubmitting(true)

      if (isEditMode && branchId) {
        const result = await dispatch(
          updateBranchThunk({
            id: branchId,
            data: {
              name: values.name,
              manager_id: values.manager_id || null,
              legal_entity_name: values.legal_entity_name || null,
              vertical: values.vertical || null
            }
          })
        )
        if (updateBranchThunk.rejected.match(result)) {
          setError(
            extractErrorMessage(result.payload, "Failed to save branch.")
          )
          setSubmitting(false)
          return
        }
        // Time zone lives on the configuration endpoint, not
        // `BranchUpdateSerializer` — sent unconditionally (a no-op PATCH
        // when unchanged) rather than diffed, matching
        // `BranchConfigurationTab`'s own direct-service-call convention.
        const configResult = await updateBranchConfiguration(branchId, {
          timezone: values.timezone
        })
        if (!configResult.ok) {
          setError(
            extractErrorMessage(
              configResult.data,
              "Failed to save the time zone."
            )
          )
          setSubmitting(false)
          return
        }
        // `BranchUpdateSerializer`'s response is a sparse subset of the
        // branch shape (it carries no `id`, no nested `manager`), so the
        // slice's own id-matched merge can't apply it — refetch the list
        // the same way the create path already does rather than trust the
        // PATCH response to update `managedBranches` in place.
        await dispatch(fetchBranches({ include_archived: true }))
        setSubmitting(false)
        if (onSaved) onSaved()
        else onClose()
        return
      }

      const result = await dispatch(
        createBranchThunk({
          name: values.name,
          city: values.city,
          timezone: values.timezone,
          manager_id: values.manager_id || null,
          legal_entity_name: values.legal_entity_name || null,
          vertical: values.vertical || null,
          start_mode: isCopyMode ? "copy" : startingSettings,
          copy_from_branch_id: isCopyMode ? startingSettings : null
        })
      )
      if (createBranchThunk.rejected.match(result)) {
        setError(
          extractErrorMessage(result.payload, "Failed to create branch.")
        )
        setSubmitting(false)
        return
      }
      await dispatch(fetchBranches({ include_archived: true }))
      setSubmitting(false)
      if (onSaved) onSaved()
      else onClose()
    } catch {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title={isEditMode ? "Edit branch" : "Add branch"}
      open={open}
      onCancel={requestClose}
      onOk={handleSubmit}
      okText={isEditMode ? "Save changes" : "Create branch"}
      confirmLoading={submitting}
      // Backdrop click must never discard entered (or even empty) values —
      // the reported bug. Escape/X/Cancel go through `requestClose`
      // instead, which asks first whenever the form is actually dirty.
      maskClosable={false}
      closable={!submitting}
      cancelButtonProps={{ disabled: submitting }}
      destroyOnClose
    >
      {!isEditMode && isFirstAdditionalBranch && existingBranches[0] && (
        <Alert
          className="mb-4"
          type="info"
          showIcon
          message={`Your existing leads, projects, files and financial records will remain in ${existingBranches[0].name}.`}
          description="The new branch will start with no operational records — nothing is copied automatically."
        />
      )}
      {error && (
        <Alert className="mb-4" type="error" showIcon message={error} />
      )}
      <Form
        form={form}
        layout="vertical"
        onValuesChange={() => {
          if (!prefillingRef.current) setDirty(true)
        }}
      >
        <Form.Item
          name="name"
          label="Branch name"
          rules={[{ required: true, message: "Branch name is required" }]}
        >
          <TextInput placeholder="e.g. Los Angeles" />
        </Form.Item>
        {!isEditMode && (
          <Form.Item name="city" label="City / location">
            <TextInput placeholder="e.g. Los Angeles, CA" />
          </Form.Item>
        )}
        <Form.Item
          name="timezone"
          label="Time zone"
          rules={[{ required: true, message: "Time zone is required" }]}
          initialValue={gcc?.multi_branch_enabled ? undefined : "UTC"}
        >
          <TimezoneSelect />
        </Form.Item>
        <Form.Item name="manager_id" label="Branch manager (optional)">
          <Select
            allowClear
            placeholder="Assign later"
            options={managers.map((m) => ({
              value: m.id,
              label: `${m.first_name} ${m.last_name}`
            }))}
          />
        </Form.Item>
        <Form.Item
          name="vertical"
          label="Vertical / service specialization (optional)"
        >
          <TextInput placeholder="e.g. Residential Remodeling, Commercial" />
        </Form.Item>
        <Form.Item name="legal_entity_name" label="Legal entity (optional)">
          <AutoComplete
            options={legalEntityNames.map((name) => ({ value: name }))}
            placeholder="Select an existing legal entity, or type a new one"
            filterOption={(inputValue, option) =>
              (option?.value as string)
                ?.toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
        {/* Not a Form.Item field: driven entirely by local state, since the
            preview panel below needs to react to it and Form.Item's own
            value/onChange injection would fight a controlled Select.
            Hidden in edit mode — a starting-settings choice only makes
            sense at creation. */}
        {!isEditMode && (
          <>
            <div className="mb-1">
              <label className="ant-form-item-label">
                <Text>Starting settings</Text>
              </label>
              <Select
                className="w-full"
                value={startingSettings}
                onChange={handleStartingSettingsChange}
                options={[
                  {
                    value: START_HQ_DEFAULTS,
                    label: "Use Company HQ defaults (recommended)"
                  },
                  ...(existingBranches.length > 0
                    ? [
                        {
                          label: "Copy from an existing branch",
                          options: existingBranches.map((b) => ({
                            value: b.id,
                            label: `Copy settings from ${b.name}`
                          }))
                        }
                      ]
                    : []),
                  { value: START_SEPARATE, label: "Configure separately" }
                ]}
              />
            </div>

            <Card size="small" className="mb-2 bg-gray-50">
              <Text type="secondary" className="text-xs block mb-2">
                {isCopyMode
                  ? `Independent overrides are created from ${copySource?.name ?? "the selected branch"}'s current settings — this branch will not track later changes to it.`
                  : STARTING_SETTINGS_HELP[startingSettings]}
              </Text>
              <div className="text-xs flex flex-col gap-1">
                <div>
                  <Text strong>Configurations</Text> —{" "}
                  {isCopyMode
                    ? `copied as independent overrides from ${copySource?.name ?? "that branch"}`
                    : startingSettings === START_HQ_DEFAULTS
                      ? "inherits Company HQ defaults (time zone, working days, business hours)"
                      : "starts from safe system defaults, independent of Company HQ"}
                </div>
                <div>
                  <Text strong>Required Information</Text> — company-wide; not
                  yet customizable per branch
                </div>
                <div>
                  <Text strong>Templates</Text> — company-wide; not yet
                  customizable per branch
                </div>
              </div>
            </Card>
          </>
        )}
      </Form>
    </Modal>
  )
}
